/**
 * React Compile Utility
 * Simple compatibility layer for socrates-question.js
 */

/**
 * Simple React compilation - returns a linking function like Angular's $compile
 * Since we're using React components, we don't need to actually compile anything
 * but we need to match the Angular API signature: $compile(element)(scope)
 */
export const reactCompile = (element) => {
  // Return a linking function that matches Angular's $compile signature
  // In React, we don't need to do anything here since components are already rendered
  return function(scope) {
    // No-op: React components are already rendered via window.StudentComponents
    // This is just for compatibility with the existing code that expects $compile(element)(scope)
    return element;
  };
};

/**
 * Simple React invoke - minimal compatibility
 */
export const reactInvoke = (callback) => {
  try {
    const mockInjector = {
      invoke: (fn) => {
        const mockServices = {
          $compile: reactCompile,
          $scope: {},
          $rootScope: {}
        };
        return fn(mockServices);
      }
    };
    return callback(mockInjector);
  } catch (error) {
    console.error('Error in reactInvoke:', error);
    return null;
  }
};

/**
 * Simple React element - minimal compatibility
 */
export const reactElement = (selector) => {
  const element = typeof selector === 'string' ? 
    document.querySelector(selector) : selector;
  
  if (!element) {
    console.warn('Element not found:', selector);
    return null;
  }

  return {
    element: element,
    injector: () => ({
      invoke: (callback) => reactInvoke(callback)
    }),
    scope: () => ({})
  };
};

/**
 * Initialize React compilation system
 */
export const initReactCompilation = () => {
  window.reactCompile = reactCompile;
  window.reactInvoke = reactInvoke;
  window.reactElement = reactElement;
  
  // Mock Angular for backward compatibility
  window.angular = {
    element: reactElement,
    compile: reactCompile
  };
  
  console.log('React compilation system initialized');
};

/**
 * Cleanup React compilation system
 */
export const cleanupReactCompilation = () => {
  delete window.reactCompile;
  delete window.reactInvoke;
  delete window.reactElement;
  delete window.angular;
  
  console.log('React compilation system cleaned up');
};
